import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ContentHistory } from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';

import { extractUserAlias } from '../../api/UtilsApi';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { isPublicationViewable } from '../../model/WorkflowViewability';
import { workflowStateDisplayNames } from '../utils/DisplayNames';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

export interface HistoryRow {
  readonly contentId: string;
  readonly workflowId: number;
  readonly authorName: string;
  readonly authorAlias: string | undefined;
  readonly publicationDate: DateTime;
  readonly status: string;
  /** `/view` link for versions that can be opened, or undefined when this version isn't viewable. */
  readonly viewHref: string | undefined;
  /** False when the version has no published body to diff, e.g. an unpublished version. */
  readonly isComparable: boolean;
}

export interface DiffData {
  readonly oldValue: object;
  readonly newValue: object;
  readonly leftTitle: string;
  readonly rightTitle: string;
}

export interface HistoryTabViewModel {
  readonly isLoading: boolean;
  readonly loadError: string | undefined;
  readonly retry: () => void;
  readonly rows: readonly HistoryRow[];
  readonly isEmpty: boolean;
  readonly versionA: number | undefined;
  readonly versionB: number | undefined;
  readonly selectVersionA: (workflowId: number) => void;
  readonly selectVersionB: (workflowId: number) => void;
  readonly isCompareEnabled: boolean;
  readonly isComparing: boolean;
  readonly compareError: string | undefined;
  readonly compare: () => void;
  readonly diffData: DiffData | undefined;
}

function toHistoryRow(item: ContentHistory): HistoryRow {
  const alias = extractUserAlias(item.ownerId);
  // Only publication-backed versions get a link; the route opens them via
  // mode=publication so it never mistakes another workflow kind for a publication.
  const isViewable = isPublicationViewable(item.state);
  const viewHref = isViewable
    ? `/view/${item.contentId}?workflowId=${item.workflowId}&mode=publication`
    : undefined;
  return {
    contentId: item.contentId,
    workflowId: item.workflowId,
    authorName: item.ownerProfile ? `${item.ownerProfile.name} (${alias})` : item.ownerId,
    authorAlias: alias !== item.ownerId ? alias : undefined,
    publicationDate: item.publicationDate,
    status: workflowStateDisplayNames.get(item.state) ?? 'Unknown',
    viewHref,
    isComparable: isViewable,
  };
}

export function useHistoryTabViewModel(): HistoryTabViewModel | undefined {
  const editorService = useContentEditorService();
  const mpl = useMplServiceClient();
  const document = useLoadedDocument();
  const { canSeeActivityForContent } = useCurrentUser();

  const contentId = document?.contentId;

  // When the document or the user changes, re-evaluate whether unpublished
  // versions are visible — they're limited to the content's authors and staff roles.
  const includeUnpublished = useMemo(
    () =>
      document !== undefined &&
      canSeeActivityForContent(
        document.author.userId,
        document.coAuthors.map((coAuthor) => coAuthor.userId),
      ),
    [canSeeActivityForContent, document],
  );

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [history, setHistory] = useState<readonly ContentHistory[]>([]);
  const [versionA, setVersionA] = useState<number | undefined>(undefined);
  const [versionB, setVersionB] = useState<number | undefined>(undefined);
  const [isComparing, setIsComparing] = useState(false);
  const [compareError, setCompareError] = useState<string | undefined>(undefined);
  const [diffData, setDiffData] = useState<DiffData | undefined>(undefined);

  const cancelRef = useRef<(() => void) | undefined>(undefined);

  const fetchHistory = useCallback(() => {
    if (!contentId) return;
    // Cancel any in-flight request from a previous call.
    cancelRef.current?.();
    let cancelled = false;
    cancelRef.current = () => {
      cancelled = true;
    };

    setIsLoading(true);
    setLoadError(undefined);
    mpl
      .listWorkflowHistory(contentId, { includeUnpublished })
      .then(({ workflows }) => {
        if (cancelled) return;
        setHistory(workflows);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError('Failed to load publication history.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });
  }, [contentId, mpl, includeUnpublished]);

  // When the component mounts, fetch publication history. Cancel on unmount.
  useEffect(() => {
    fetchHistory();
    return () => {
      cancelRef.current?.();
    };
  }, [fetchHistory]);

  const rows = useMemo(() => history.map(toHistoryRow), [history]);
  const isEmpty = !isLoading && rows.length === 0;
  const isCompareEnabled =
    versionA !== undefined && versionB !== undefined && versionA !== versionB;

  const selectVersionA = useCallback((workflowId: number) => {
    setVersionA(workflowId);
    setDiffData(undefined);
  }, []);

  const selectVersionB = useCallback((workflowId: number) => {
    setVersionB(workflowId);
    setDiffData(undefined);
  }, []);

  // When the user clicks "Compare Versions", fetch both publications and compute diff data.
  const compare = useCallback(() => {
    if (!contentId) return;
    if (versionA === undefined || versionB === undefined || versionA === versionB) return;

    const [older, newer] = versionA < versionB ? [versionA, versionB] : [versionB, versionA];
    setIsComparing(true);
    setCompareError(undefined);
    Promise.all([
      mpl.getPublicationByWorkflow(contentId, older),
      mpl.getPublicationByWorkflow(contentId, newer),
    ])
      .then(([oldPub, newPub]) => {
        setDiffData({
          oldValue: oldPub,
          newValue: newPub,
          leftTitle: `Version ${older}.0`,
          rightTitle: `Version ${newer}.0`,
        });
      })
      .catch(() => {
        setCompareError('Failed to load versions for comparison.');
      })
      .finally(() => {
        setIsComparing(false);
      });
  }, [contentId, mpl, versionA, versionB]);

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  return {
    isLoading,
    loadError,
    retry: fetchHistory,
    rows,
    isEmpty,
    versionA,
    versionB,
    selectVersionA,
    selectVersionB,
    isCompareEnabled,
    isComparing,
    compareError,
    compare,
    diffData,
  };
}
